import React from 'react'
import { IEvent } from '../../store/events/interfaces'
import { Badge, Button, Card } from 'react-bootstrap'
import { RootState } from '../../store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EventAge } from './EventAge'
import { EventOccupation } from './EventOccupation'
import { useNavigate } from 'react-router-dom'
import { RegisterButton } from '../shared/RegisterButton'
import { WorkflowStatusBadge } from '../shared/WorkflowStatusBadge'
import { useSelector } from 'react-redux'
import { InfoBadges } from '../shared/Badges'
import { useNumPaymentsEnabled } from '../../functions/registrations/checkPaymentStageSkip'

const EventCard = (props: { event: IEvent; hasRegistration: boolean }) => {
  const navigate = useNavigate()
  const { event, hasRegistration } = props
  const { data: program } = useSelector((state: RootState) => state.program)
  const { testMode } = useSelector((state: RootState) => state.institution)
  const isExternal = event.has_external_registration
  const hasExternalDescription = event.external_registration_description_html && event.external_registration_description_html.trim();
  const hasPaymentMethods = useNumPaymentsEnabled() > 0

  return (
    <Card
      className={`${hasRegistration ? 'border-success' : 'border-dark'} h-100`}
      id={'event-' + event.relative_id}
      tabIndex={0}
    >
      {!!event.cover_photo && program.enable_preview_images && (
        <Card.Img
          variant="top"
          src={event.cover_photo.medium}
          alt={
            event.cover_photo.caption
              ? event.cover_photo.caption
              : `Vorschaubild der Veranstaltung ${event.name}`
          }
        />
      )}
      {testMode && <WorkflowStatusBadge event={event}></WorkflowStatusBadge>}
      <Card.Header
        className={`${hasRegistration && 'dark-success text-white'}`}
      >
        <strong>
          {event.name} (#{event.relative_id})
        </strong>
        <br />
        <small>
          {event.duration_summary
            ? event.duration_summary
            : 'Veranstaltung ohne Zeitraum'}
        </small>
        <br />
        <InfoBadges event={event} hasRegistration={hasRegistration} tooltipPlacement={"bottom"} />
      </Card.Header>
      <Card.Body>
        <strong>
          {program.requires_family_data
            ? 'Altersempfehlung'
            : 'Altersbeschränkung'}
        </strong>
        <p>
          <EventAge
            event={event}
            isFamilyRequired={program.requires_family_data}
          />
        </p>
        {program.booking_visibility !== 'never' &&
        !program.hide_event_occupation ? (
          <>
            <hr />
            <strong>Belegung</strong>
            <div className="mt-3">
              <EventOccupation event={event} isModal={false} />
            </div>
            <hr />
          </>
        ) : (
          <></>
        )}
        {hasPaymentMethods && (
          <>
            <hr />
            <p>
              <strong>Kosten</strong>
            </p>
            {event.price_categories.length > 0 ? (
              <>
                <ul className="list-unstyled">
                  <li>{event.display_price}</li>
                  {event.price_categories.map((item, index) => (
                    <li key={index}>
                      {item.name}: {item.price.toFixed(2)}&nbsp;€
                    </li>
                  ))}
                </ul>
                {event.has_external_payment && (
                  <>
                    <Badge variant="warning" tabIndex={0}>
                      Achtung
                    </Badge>{' '}
                    <small className="text-muted">
                      Bezahlung wird direkt über den Veranstalter geregelt!
                    </small>
                  </>
                )}
              </>
            ) : (
              <>
                {event.price ? (
                  <>
                    {`${event.price.toFixed(2)} €`}
                    {event.has_external_payment && (
                      <>
                        <br />
                        <Badge variant="warning" tabIndex={0}>
                          Achtung
                        </Badge>{' '}
                        <small className="text-muted">
                          Bezahlung wird direkt über den Veranstalter geregelt!
                        </small>
                      </>
                    )}
                  </>
                ) : (
                  'Kostenlos'
                )}
              </>
            )}
          </>
        )}
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between flex-column">
        <RegisterButton event={event} editable={true} extraClasses={'mb-1'} />
        {(isExternal && hasExternalDescription) || !isExternal ? (
          <Button
            variant="outline-dark"
            onClick={() => {
              navigate(`/veranstaltungen/${event.event_id}${isExternal ? '?highlight=external-registration' : ''}`, { replace: true })
            }}
            className="mobile-btn"
            aria-label="Veranstaltungsdetails"
          >
            {isExternal ? 'Mehr Erfahren' : <><FontAwesomeIcon icon="info-circle" role="presentation" /> Details</>}
          </Button>
        ) : null}
      </Card.Footer>
    </Card>
  )
}

export default EventCard
